import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Package, DollarSign, Clock, CheckCircle2, RefreshCw, LogOut, ChevronDown, ChevronUp } from "lucide-react";
import { fetchOrders, fetchOrderStats, updateOrderStatus } from "../api";
import { useAuth } from "../context/AuthContext";

const STATUS_OPTIONS = ["new", "processing", "shipped", "delivered", "cancelled"];

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch (e) {
    return iso;
  }
};

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="bg-[#141210] border border-[#2c2822] rounded-lg p-6">
    <div className="flex items-center gap-3 mb-2">
      <Icon size={18} className="text-[#c9a24b]" />
      <span className="text-[#a29a8c] text-xs tracking-[0.15em] uppercase">{label}</span>
    </div>
    <p className="font-display text-4xl text-[#f4efe4]">{value}</p>
  </div>
);

const Pill = ({ children, tone = "muted" }) => {
  const tones = {
    paid: "text-green-400 border-green-500/40",
    pending: "text-amber-400 border-amber-500/40",
    muted: "text-[#a29a8c] border-[#2c2822]",
  };
  return <span className={`inline-block border rounded-full px-3 py-1 text-xs capitalize ${tones[tone] || tones.muted}`}>{children}</span>;
};

const AdminOrders = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [o, s] = await Promise.all([fetchOrders(200), fetchOrderStats()]);
      setOrders(o);
      setStats(s);
    } catch (e) {
      // errors surface via global handling
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    timer.current = setInterval(load, 15000);
    return () => clearInterval(timer.current);
  }, [load]);

  const doLogout = () => {
    clearInterval(timer.current);
    logout();
    navigate("/login");
  };

  const changeStatus = async (id, status) => {
    setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, fulfillment_status: status } : o)));
    try {
      await updateOrderStatus(id, status);
    } catch (e) {
      load();
    }
  };

  return (
    <div className="max-w-[1280px] mx-auto px-6 py-16">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <p className="text-[#c9a24b] text-sm tracking-[0.3em] uppercase mb-2">Admin</p>
          <h1 className="font-display text-4xl text-[#f4efe4]">Orders</h1>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={load} className="btn-outline-gold px-4 py-2 rounded-full text-xs tracking-[0.15em] uppercase inline-flex items-center gap-2">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button onClick={doLogout} className="text-[#a29a8c] hover:text-[#c9a24b] transition-colors inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em]">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </div>

      <div className="flex gap-4 mb-10 text-xs tracking-[0.15em] uppercase">
        <Link to="/admin/orders" className="text-[#c9a24b] border-b border-[#c9a24b] pb-1">Orders</Link>
        <Link to="/admin/inventory" className="text-[#a29a8c] hover:text-[#c9a24b] pb-1">Inventory</Link>
        <Link to="/admin/reviews" className="text-[#a29a8c] hover:text-[#c9a24b] pb-1">Reviews</Link>
        <Link to="/admin/visitors" className="text-[#a29a8c] hover:text-[#c9a24b] pb-1">Visitors</Link>
      </div>

      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
          <StatCard icon={Package} label="Total Orders" value={stats.total} />
          <StatCard icon={CheckCircle2} label="Paid" value={stats.paid} />
          <StatCard icon={Clock} label="Unpaid / Pending" value={stats.pending} />
          <StatCard icon={DollarSign} label="Revenue (Paid)" value={`$${(stats.revenue || 0).toFixed(2)}`} />
        </div>
      )}

      <div className="bg-[#141210] border border-[#2c2822] rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-[#2c2822]">
          <h2 className="font-display text-xl text-[#f4efe4]">All Orders</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[#6b6459] text-xs tracking-[0.12em] uppercase">
                <th className="text-left font-medium px-6 py-3">Order</th>
                <th className="text-left font-medium px-6 py-3">Date</th>
                <th className="text-left font-medium px-6 py-3">Customer</th>
                <th className="text-left font-medium px-6 py-3">Total</th>
                <th className="text-left font-medium px-6 py-3">Payment</th>
                <th className="text-left font-medium px-6 py-3">Fulfillment</th>
                <th className="text-left font-medium px-6 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 ? (
                <tr><td colSpan={7} className="px-6 py-10 text-center text-[#a29a8c]">No orders yet.</td></tr>
              ) : (
                orders.map((o) => {
                  const isOpen = expanded === o.id;
                  const total = o.total || o.subtotal || 0;
                  const paid = o.payment_status === "paid";
                  return (
                    <React.Fragment key={o.id}>
                      <tr className="border-t border-[#2c2822] text-[#c8c0b2] hover:bg-[#1b1815] transition-colors">
                        <td className="px-6 py-3 font-mono text-[#f4efe4]">{(o.id || "").slice(0, 8).toUpperCase()}</td>
                        <td className="px-6 py-3 whitespace-nowrap">{fmtDate(o.createdAt)}</td>
                        <td className="px-6 py-3">
                          <div className="text-[#f4efe4]">{o.customer?.name || "—"}</div>
                          <div className="text-[#6b6459] text-xs">{o.customer?.email}</div>
                        </td>
                        <td className="px-6 py-3 text-[#c9a24b]">${Number(total).toFixed(2)}</td>
                        <td className="px-6 py-3"><Pill tone={paid ? "paid" : "pending"}>{o.payment_status || "pending"}</Pill></td>
                        <td className="px-6 py-3">
                          <select
                            value={o.fulfillment_status || "new"}
                            onChange={(e) => changeStatus(o.id, e.target.value)}
                            className="bg-[#0b0a09] border border-[#2c2822] text-[#f4efe4] text-xs rounded-full px-3 py-1.5 outline-none focus:border-[#c9a24b] cursor-pointer capitalize"
                          >
                            {STATUS_OPTIONS.map((s) => (
                              <option key={s} value={s} className="bg-[#141210] capitalize">{s}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-6 py-3">
                          <button onClick={() => setExpanded(isOpen ? null : o.id)} className="text-[#a29a8c] hover:text-[#c9a24b] transition-colors inline-flex items-center gap-1 text-xs">
                            {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />} Details
                          </button>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="border-t border-[#2c2822] bg-[#0b0a09]">
                          <td colSpan={7} className="px-6 py-5">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                              <div>
                                <p className="text-[#6b6459] text-xs tracking-[0.15em] uppercase mb-3">Items</p>
                                <ul className="space-y-2">
                                  {(o.items || []).map((it, i) => (
                                    <li key={i} className="flex justify-between text-[#c8c0b2]">
                                      <span>{it.qty} × {it.name}</span>
                                      <span className="text-[#c9a24b]">${(it.price * it.qty).toFixed(2)}</span>
                                    </li>
                                  ))}
                                </ul>
                                <div className="gold-hairline my-4" />
                                <div className="flex justify-between text-[#f4efe4]">
                                  <span>Subtotal</span><span>${Number(o.subtotal || 0).toFixed(2)}</span>
                                </div>
                                {o.shipping_amount != null && (
                                  <div className="flex justify-between text-[#a29a8c] text-sm mt-1">
                                    <span>Shipping{o.shipping_method ? ` (${o.shipping_method})` : ""}</span>
                                    <span>${Number(o.shipping_amount).toFixed(2)}</span>
                                  </div>
                                )}
                                <div className="flex justify-between text-[#c9a24b] font-display text-lg mt-2">
                                  <span>Total</span><span>${Number(total).toFixed(2)}</span>
                                </div>
                              </div>
                              <div>
                                <p className="text-[#6b6459] text-xs tracking-[0.15em] uppercase mb-3">Customer & Shipping</p>
                                <div className="text-[#c8c0b2] space-y-1.5 text-sm">
                                  <p className="text-[#f4efe4]">{o.customer?.name}</p>
                                  <p>{o.customer?.email}</p>
                                  {o.customer?.phone && <p>{o.customer.phone}</p>}
                                  <p className="text-[#a29a8c] mt-2">{o.customer?.address}</p>
                                  {o.shipping_address && (o.shipping_address.line1 || o.shipping_address.city) && (
                                    <p className="text-[#a29a8c] mt-2">
                                      {[o.shipping_address.line1, o.shipping_address.line2, o.shipping_address.city, o.shipping_address.state, o.shipping_address.postal_code, o.shipping_address.country].filter(Boolean).join(", ")}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-[#6b6459] text-xs mt-4">Private page — bookmark: /admin/orders</p>
    </div>
  );
};

export default AdminOrders;
